import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  ADMIN_INVITATIONS_COLLECTION,
  USERS_COLLECTION,
  firestore,
} from "../lib/firebase";
import type {
  AdminClaims,
  AdminInvitation,
  AdminInvitationDraft,
  AppUserProfile,
} from "../types/auth";
import {
  asRecord,
  defaultCapabilitiesForRole,
  normalizeAdminRole,
  normalizeCapabilities,
  normalizeEmail,
  normalizeInvitationStatus,
  toDate,
} from "../utils/firestoreData";

function normalizeClaims(claims: Record<string, unknown>): AdminClaims {
  return {
    globalAdmin: claims.globalAdmin === true,
    canCreateFormation: claims.canCreateFormation === true,
    canCreateSession: claims.canCreateSession === true,
  };
}

export function readAdminClaims(rawClaims: Record<string, unknown>): AdminClaims {
  return normalizeClaims(rawClaims);
}

function normalizeProfile(uid: string, data: Record<string, unknown>): AppUserProfile {
  const role = normalizeAdminRole(data.role);

  return {
    accountType:
      data.accountType === "participant" || data.accountType === "mixed"
        ? data.accountType
        : "admin",
    capabilities: normalizeCapabilities(
      data.capabilities ?? defaultCapabilitiesForRole(role),
    ),
    createdAt: toDate(data.createdAt),
    disabledAt: toDate(data.disabledAt),
    displayName: typeof data.displayName === "string" ? data.displayName : "",
    email: typeof data.email === "string" ? data.email : "",
    invitationId:
      typeof data.invitationId === "string" ? data.invitationId : null,
    lastLoginAt: toDate(data.lastLoginAt),
    role,
    status:
      data.status === "suspended" || data.status === "disabled"
        ? data.status
        : "active",
    uid,
    updatedAt: toDate(data.updatedAt),
  };
}

function normalizeInvitation(
  id: string,
  data: Record<string, unknown>,
): AdminInvitation {
  const role = normalizeAdminRole(data.role);

  return {
    acceptedAt: toDate(data.acceptedAt),
    acceptedBy: typeof data.acceptedBy === "string" ? data.acceptedBy : null,
    capabilities: normalizeCapabilities(
      data.capabilities ?? defaultCapabilitiesForRole(role),
    ),
    createdAt: toDate(data.createdAt),
    createdBy: typeof data.createdBy === "string" ? data.createdBy : null,
    displayName: typeof data.displayName === "string" ? data.displayName : "",
    email: typeof data.email === "string" ? data.email : "",
    id,
    role,
    status: normalizeInvitationStatus(data.status),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function loadUserProfile(uid: string): Promise<AppUserProfile | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(doc(firestore, USERS_COLLECTION, uid));

  if (!snapshot.exists()) {
    return null;
  }

  return normalizeProfile(uid, asRecord(snapshot.data()));
}

export async function touchAdminLogin(user: User): Promise<AppUserProfile | null> {
  if (!firestore || !user.email) {
    return null;
  }

  const profileRef = doc(firestore, USERS_COLLECTION, user.uid);
  const snapshot = await getDoc(profileRef);

  if (!snapshot.exists()) {
    return null;
  }

  await setDoc(
    profileRef,
    {
      email: normalizeEmail(user.email),
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  const nextSnapshot = await getDoc(profileRef);
  return normalizeProfile(user.uid, asRecord(nextSnapshot.data()));
}

export async function createProfileFromInvitation({
  displayName,
  invitation,
  user,
}: {
  displayName: string;
  invitation: AdminInvitation;
  user: User;
}): Promise<AppUserProfile> {
  if (!firestore || !user.email) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const email = normalizeEmail(user.email);

  if (email !== invitation.email || invitation.status !== "pending") {
    throw new Error("Cette invitation n'est pas valide pour ce compte.");
  }

  const profileRef = doc(firestore, USERS_COLLECTION, user.uid);

  await setDoc(
    profileRef,
    {
      accountType: "admin",
      capabilities: invitation.capabilities,
      createdAt: serverTimestamp(),
      displayName: displayName.trim() || invitation.displayName || email,
      email,
      invitationId: invitation.id,
      lastLoginAt: serverTimestamp(),
      role: invitation.role,
      status: "active",
      uid: user.uid,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  await updateDoc(doc(firestore, ADMIN_INVITATIONS_COLLECTION, invitation.id), {
    acceptedAt: serverTimestamp(),
    acceptedBy: user.uid,
    status: "accepted",
    updatedAt: serverTimestamp(),
  });

  const nextSnapshot = await getDoc(profileRef);
  return normalizeProfile(user.uid, asRecord(nextSnapshot.data()));
}

export async function loadAdminInvitation(
  invitationId: string,
): Promise<AdminInvitation | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(
    doc(firestore, ADMIN_INVITATIONS_COLLECTION, invitationId),
  );

  if (!snapshot.exists()) {
    return null;
  }

  return normalizeInvitation(snapshot.id, asRecord(snapshot.data()));
}

export async function listAdminInvitations(): Promise<AdminInvitation[]> {
  if (!firestore) {
    return [];
  }

  const invitationsQuery = query(
    collection(firestore, ADMIN_INVITATIONS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(invitationsQuery);

  return snapshot.docs.map((invitationDoc) =>
    normalizeInvitation(invitationDoc.id, asRecord(invitationDoc.data())),
  );
}

export async function createAdminInvitation({
  actorUid,
  draft,
}: {
  actorUid: string;
  draft: AdminInvitationDraft;
}): Promise<AdminInvitation> {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  const email = normalizeEmail(draft.email);

  if (!email) {
    throw new Error("L'email est obligatoire.");
  }

  const invitationId = crypto.randomUUID();
  const role = normalizeAdminRole(draft.role);
  const invitationRef = doc(
    firestore,
    ADMIN_INVITATIONS_COLLECTION,
    invitationId,
  );

  await setDoc(invitationRef, {
    capabilities: draft.capabilities,
    createdAt: serverTimestamp(),
    createdBy: actorUid,
    displayName: draft.displayName.trim(),
    email,
    role,
    status: "pending",
    updatedAt: serverTimestamp(),
  });

  const snapshot = await getDoc(invitationRef);
  return normalizeInvitation(invitationId, asRecord(snapshot.data()));
}
